import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  TIER_LABELS,
  TIER_COLORS,
  scoreToColor,
  scoreToBadge,
  FUNCTION_COLORS,
  PRIORITY_BADGE,
} from "@/lib/constants";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Shield,
  AlertTriangle,
  FileCheck,
  Building2,
  ClipboardCheck,
  TrendingUp,
} from "lucide-react";

function StatTile({ label, value, sub, icon: Icon, accent = "text-white" }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="label-uppercase">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-zinc-500" aria-hidden />}
      </div>
      <div className={`mt-3 text-3xl tracking-tight font-medium mono ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function TierBadge({ tier }) {
  const c = TIER_COLORS[tier] || TIER_COLORS[1];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${c.border} ${c.bg} ${c.text}`} data-testid="tier-badge">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.solid }} />
      {TIER_LABELS[tier]}
    </span>
  );
}

function frameworkShortLabel(code) {
  if (code === "NIST_CSF_2_0") return "NIST";
  if (code === "ISO_27001_2022") return "ISO";
  if (code === "SOC_2") return "SOC 2";
  return code || "FW";
}

function sectionColor(code, score) {
  const fixed = {
    GV: "#8B5CF6",
    ID: "#3B82F6",
    PR: "#06B6D4",
    DE: "#10B981",
    RS: "#F59E0B",
    RC: "#EF4444",
    ISO: "#38BDF8",
    SOC: "#A78BFA",
  };

  if (fixed[code]) return fixed[code];
  return scoreToColor(score);
}

function getProfileItems(activeFramework, scores) {
  if (!scores) return [];

  const code = activeFramework?.code;

  if (code === "NIST_CSF_2_0") {
    return (scores.functions || []).map((f) => ({
      key: f.function_code,
      label: f.function_name,
      score: f.score,
      target: f.target_score,
      count: f.subcategory_count,
      color: FUNCTION_COLORS[f.function_code] || scoreToColor(f.score),
    }));
  }

  return (scores.categories || []).map((c) => ({
    key: c.category_code,
    label: c.category_name,
    score: c.score,
    target: c.target_score,
    count: c.subcategory_count,
    color: sectionColor(activeFramework?.code === "ISO_27001_2022" ? "ISO" : "SOC", c.score),
  }));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState("NIST_CSF_2_0");

  useEffect(() => {
    let on = true;
    (async () => {
      setLoading(true);
      try {
        const [s, h] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/dashboard/history"),
        ]);
        if (!on) return;
        setData(s.data);
        setHistory(h.data.points || []);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="dashboard-loading">Loading dashboard…</div>;

  if (!data?.has_data) {
    return (
      <div data-testid="dashboard-empty">
        <Header title="Executive Dashboard" subtitle="No assessments yet" user={user} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <StatTile label="Clients" value={data?.org_stats?.total_clients ?? 0} icon={Building2} />
          <StatTile label="Assessments" value={data?.org_stats?.total_assessments ?? 0} icon={ClipboardCheck} />
          <StatTile label="Completed" value={data?.org_stats?.completed_assessments ?? 0} icon={FileCheck} />
          <StatTile label="In Progress" value={data?.org_stats?.in_progress_assessments ?? 0} icon={TrendingUp} />
        </div>
        <div className="mt-10 border border-zinc-800 rounded-md p-12 text-center bg-[#121212]">
          <div className="label-uppercase mb-2">No data</div>
          <h3 className="text-lg text-white mb-1">Start your first assessment</h3>
          <p className="text-sm text-zinc-500">Create a client, then run a cybersecurity maturity assessment.</p>
          <Link to="/clients" className="inline-block mt-4 px-4 py-2 rounded-md bg-white text-black text-sm hover:bg-zinc-200" data-testid="empty-cta-clients">
            Manage Clients →
          </Link>
        </div>
      </div>
    );
  }

  const frameworkScores = data.framework_scores || [];
  const activeFramework =
    frameworkScores.find((f) => f.code === selectedFramework) ||
    frameworkScores.find((f) => f.code === "NIST_CSF_2_0") ||
    frameworkScores[0];

  const s = activeFramework?.scores || data.scores;
  const frameworkLabel = activeFramework
    ? `${activeFramework.name || activeFramework.code} ${activeFramework.version || ""}`.trim()
    : "NIST CSF 2.0";

  const profileItems = getProfileItems(activeFramework, s);

  const radarData = profileItems.map((item) => ({
    section: item.key,
    current: item.score,
    target: item.target,
    fullMark: 5,
  }));

  const activeColor = scoreToColor(s.overall_score);
  const profileTitle =
    activeFramework?.code === "NIST_CSF_2_0"
      ? "Function Scores"
      : activeFramework?.code === "ISO_27001_2022"
      ? "ISO Section Scores"
      : activeFramework?.code === "SOC_2"
      ? "SOC Criteria Scores"
      : "Section Scores";

  return (
    <div data-testid="dashboard-page">
      <Header
        title="Executive Dashboard"
        subtitle={`${data.client?.name || "—"} · ${data.assessment.name}`}
        user={user}
      />

      {frameworkScores.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="framework-grade-tabs">
          {frameworkScores.map((fw) => {
            const fs = fw.scores;
            const selected = selectedFramework === fw.code;
            const color = scoreToColor(fs.overall_score);

            return (
              <button
                key={fw.code}
                onClick={() => setSelectedFramework(fw.code)}
                className={`text-left rounded-md border p-4 transition ${
                  selected
                    ? "border-white bg-white text-black"
                    : "border-zinc-800 bg-[#121212] text-zinc-300 hover:border-zinc-600"
                }`}
                data-testid={`framework-grade-${fw.code}`}
              >
                <div className={`text-[10px] tracking-[0.25em] uppercase ${selected ? "text-zinc-700" : "text-zinc-500"}`}>
                  Framework Grade
                </div>

                <div className="mt-2 flex items-baseline justify-between gap-3">
                  <div>
                    <div className={`text-sm font-medium ${selected ? "text-black" : "text-white"}`}>
                      {fw.name} {fw.version}
                    </div>
                    <div className={`text-xs mt-1 ${selected ? "text-zinc-700" : "text-zinc-500"}`}>
                      {fs.responded_subcategories}/{fs.total_subcategories} controls assessed
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl mono font-medium" style={{ color: selected ? "#000000" : color }}>
                      {fs.overall_score.toFixed(2)}
                    </div>
                    <div className={`text-[10px] mono ${selected ? "text-zinc-700" : "text-zinc-500"}`}>
                      / 5.00
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="md:col-span-1 rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid="overall-score-card">
          <div className="label-uppercase">Overall Maturity</div>

          <div className="mt-4 flex items-baseline gap-2">
            <div className="text-5xl tracking-tight font-medium mono" style={{ color: activeColor }}>
              {s.overall_score.toFixed(2)}
            </div>
            <div className="text-zinc-600 mono text-lg">/ 5.00</div>
          </div>

          <div className="mt-3"><TierBadge tier={s.overall_tier} /></div>

          <div className="mt-3 text-xs text-zinc-500 leading-relaxed">
            Target: <span className="text-white mono">{s.overall_target_score.toFixed(2)}</span> ·
            Gap: <span className="text-white mono">{Math.max(0, s.overall_target_score - s.overall_score).toFixed(2)}</span>
          </div>

          {s.has_critical_violation && (
            <div className="mt-3 flex items-start gap-2 text-xs text-red-400 bg-red-500/5 border border-red-500/20 rounded-md p-2.5" data-testid="critical-violation-alert">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Critical control deficiency — overall tier capped at Tier 2.
            </div>
          )}
        </div>

        <StatTile label="Controls Assessed" value={`${s.responded_subcategories}/${s.total_subcategories}`} sub={frameworkLabel} icon={ClipboardCheck} />
        <StatTile label="Evidence Completion" value={`${s.evidence_completion_pct}%`} sub="of controls with evidence" icon={FileCheck} />
        <StatTile label="Critical Gaps" value={s.critical_gaps.length} sub={`${data.remediation_summary?.by_priority?.critical || 0} critical remediation items`} icon={AlertTriangle} accent={s.critical_gaps.length ? "text-red-400" : "text-white"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid="function-radar-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-uppercase">{frameworkLabel} Profile</div>
              <h3 className="text-sm text-white mt-1">Current vs Target Maturity</h3>
            </div>
            <Shield className="w-4 h-4 text-zinc-500" aria-hidden />
          </div>

          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#27272A" />
                <PolarAngleAxis
                  dataKey="section"
                  tick={{ fill: "#D4D4D8", fontSize: 12, fontWeight: 700 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 5]}
                  tick={{ fill: "#71717A", fontSize: 10 }}
                  stroke="#27272A"
                />
                <Radar
                  name="Target"
                  dataKey="target"
                  stroke="#71717A"
                  fill="#71717A"
                  fillOpacity={0.08}
                  strokeDasharray="4 4"
                />
                <Radar
                  name="Current"
                  dataKey="current"
                  stroke={activeColor}
                  fill={activeColor}
                  fillOpacity={0.28}
                  strokeWidth={2}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 6 }}
                  formatter={(value) => Number(value).toFixed(2)}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid="function-scores-card">
          <div className="label-uppercase mb-3">{profileTitle}</div>

          <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
            {profileItems.map((item) => (
              <div key={item.key} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: item.color }} />
                    <span className="text-white font-medium mono shrink-0">{item.key}</span>
                    <span className="text-zinc-500 truncate">{item.label}</span>
                  </div>
                  <span className="mono text-white shrink-0">{item.score.toFixed(2)}</span>
                </div>

                <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden border border-zinc-800">
                  <div
                    className="h-full"
                    style={{
                      width: `${Math.min(100, (item.score / 5) * 100)}%`,
                      background: scoreToColor(item.score),
                    }}
                  />
                </div>

                <div className="flex justify-between text-[10px] text-zinc-600 mono">
                  <span>{item.count} controls</span>
                  <span>target {item.target.toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-zinc-800 bg-[#121212] p-5 mt-4" data-testid="category-heatmap">
        <div className="flex items-center justify-between mb-3">
          <div className="label-uppercase">
            {activeFramework?.code === "NIST_CSF_2_0" ? "Category Heatmap" : "Section Heatmap"}
          </div>
          <div className="text-[10px] text-zinc-500 mono">
            {frameworkShortLabel(activeFramework?.code)}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
          {(s.categories || []).map((c) => {
            const color = scoreToColor(c.score);
            return (
              <div
                key={c.category_code}
                className="p-3 rounded-md border"
                style={{ background: color + "12", borderColor: color + "40" }}
              >
                <div className="label-uppercase">{c.category_code}</div>
                <div className="text-[11px] text-zinc-300 mt-1 line-clamp-2 leading-tight">
                  {c.category_name}
                </div>

                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-xl mono font-medium" style={{ color }}>
                    {c.score.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-zinc-500 mono">
                    / {c.target_score.toFixed(1)}
                  </span>
                </div>

                <div className="mt-2 h-1.5 bg-zinc-950 rounded-full overflow-hidden border border-zinc-800">
                  <div
                    className="h-full"
                    style={{
                      width: `${Math.min(100, (c.score / 5) * 100)}%`,
                      background: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid="lowest-subcategories">
          <div className="flex items-center justify-between mb-3">
            <div className="label-uppercase">Lowest Scoring Controls</div>
            <span className="text-xs text-zinc-500">Bottom 10</span>
          </div>

          <div className="space-y-2">
            {s.lowest_subcategories.slice(0, 10).map((sub) => (
              <div key={sub.subcategory_id} className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-md border border-zinc-800 hover:border-zinc-700 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs mono text-white font-medium">{sub.subcategory_code}</span>
                    {sub.critical && <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-[10px] py-0">CRITICAL</Badge>}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{sub.description}</div>
                </div>

                <div className="text-right flex-shrink-0">
                  <div className="text-lg mono font-medium" style={{ color: scoreToColor(sub.adjusted_score) }}>{sub.adjusted_score.toFixed(1)}</div>
                  <div className="text-[10px] text-zinc-500 mono">→ {sub.target_score.toFixed(1)}</div>
                </div>
              </div>
            ))}

            {s.lowest_subcategories.length === 0 && <div className="text-xs text-zinc-500">No data for this framework yet.</div>}
          </div>
        </div>

        <div className="rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid="remediation-priorities">
          <div className="flex items-center justify-between mb-3">
            <div className="label-uppercase">Remediation Priorities</div>
            <Link to="/remediation" className="text-xs text-zinc-400 hover:text-white" data-testid="view-all-remediation">View all →</Link>
          </div>

          <div className="space-y-2">
            {(data.remediation_summary?.top || []).map((r) => (
              <div key={r.id} className="px-3 py-2.5 rounded-md border border-zinc-800">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs mono text-white font-medium">{r.subcategory_code}</span>
                      <Badge className={`${PRIORITY_BADGE[r.priority]} text-[10px] py-0`}>{r.priority.toUpperCase()}</Badge>
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{r.title}</div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] text-zinc-500">Gap</div>
                    <div className="text-base mono font-medium text-orange-400">{r.gap.toFixed(1)}</div>
                  </div>
                </div>
              </div>
            ))}

            {(!data.remediation_summary?.top || data.remediation_summary.top.length === 0) && <div className="text-xs text-zinc-500">No remediation tasks.</div>}
          </div>
        </div>
      </div>

      {history.length > 1 && (
        <div className="rounded-md border border-zinc-800 bg-[#121212] p-5 mt-4" data-testid="history-trend-card">
          <div className="label-uppercase mb-3">Maturity Trend</div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history.map((p) => ({ date: p.date, score: p.overall_score, ...p.functions }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" tick={{ fill: "#A1A1AA", fontSize: 11 }} stroke="#27272A" />
                <YAxis domain={[0, 5]} tick={{ fill: "#A1A1AA", fontSize: 11 }} stroke="#27272A" />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="score" stroke="#FAFAFA" strokeWidth={2} dot={{ r: 4 }} name="Overall" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({ title, subtitle, user }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3" data-testid="dashboard-header">
      <div>
        <div className="label-uppercase">Executive Overview</div>
        <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">{title}</h1>
        <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>
      </div>
      <div className="text-xs text-zinc-500 mono">{user?.email}</div>
    </div>
  );
}
